import {
    GameEvent,
    isPlayerAction,
    isRemovePlayerEvent,
    PlayerAction,
    PlayerId,
    Projection, RemovePlayerEvent,
    SharedGameState,
    useGameStore,
} from '@glark-newco/game-library';
import {useMemo} from 'react';
import {TileId} from './signlinesBoardSlice';
import {
    isCrunchTimePayload,
    isDealTilesEvent,
    isPlayTileEvent,
    isRemoveTileEvent,
    isTilePayload,
} from '../SignlinesPayloadTypes';


interface RollingStats {
    dealtTiles: TileId[],
    correctTilesCount: number,
    incorrectTilesCount: number,
    removedTilesCount: number,
    crunchTimePlays: number,
    handEmptySeq: number | undefined;
    allAccurateSeq: number | undefined;
}

export interface FinalStats {
    handEmptySequence: number;
    allAccurateSequence: number;
    removedTilesCount: number;
    crunchTimeCount: number;
}

export type RollingStatsMap = Record<PlayerId, RollingStats>;
export type FinalStatsMap = Record<PlayerId, FinalStats>;


function findPlayerDealt(tileId: TileId, playerState: RollingStatsMap): PlayerId {
    const entry = Object.entries(playerState).find(([, state]) => state.dealtTiles.includes(tileId));
    if (!entry)
        throw new Error(`Error tile [${tileId}] hasn't been dealt.`, playerState);
    return  entry[0];
}

const emptyRollingStats = {
    dealtTiles: [],
    correctTilesCount: 0,
    incorrectTilesCount: 0,
    removedTilesCount: 0,
    crunchTimePlays: 0,
    handEmptySeq: undefined,
    allAccurateSeq: undefined,
} as RollingStats;

export interface PlayerStatsSlice extends SharedGameState {
    rollingStatsMap?: RollingStatsMap;
    crunched?: boolean;
}

function updateState(
    playerState: RollingStatsMap,
    action: PlayerAction,
    crunched: boolean,
): RollingStatsMap {
    if (isDealTilesEvent(action.payload)) {
        const playerId = action.payload.playerId;
        const existing = playerState[playerId] ?? emptyRollingStats;
        return {
            ...playerState,
            [playerId]: {
                ...existing,
                dealtTiles: [...existing.dealtTiles, ...action.payload.tileIds],
            },
        };
    }
    if (isPlayTileEvent(action.payload)) {
        const playerId = action.payload.playerId;
        const correct = action.payload.tileId === action.payload.positionId;
        const existing = playerState[playerId] ?? emptyRollingStats;

        const newCorrect = existing.correctTilesCount + (correct ? 1 : 0);
        const newIncorrect = existing.incorrectTilesCount + (correct ? 0 : 1);
        const handSize = existing.dealtTiles.length;
        const handEmptyNow = handSize <= newCorrect + newIncorrect;
        const allAccNow = handSize <= newCorrect;
        return {
            ...playerState,
            [playerId]: {
                ...existing,
                correctTilesCount: newCorrect,
                incorrectTilesCount: newIncorrect,
                crunchTimePlays: existing.crunchTimePlays + (crunched ? 1 : 0),
                handEmptySeq: existing.handEmptySeq ?? (handEmptyNow ? action.sequence : undefined),
                allAccurateSeq: existing.allAccurateSeq ?? (allAccNow ? action.sequence : undefined),
            },
        };
    }
    if (isRemoveTileEvent(action.payload)) {
        if (!action.payload.tileId) return playerState;  // Shouldn't be possible. Double click?
        const playerId = findPlayerDealt(action.payload.tileId, playerState);
        const correct = action.payload.tileId === action.payload.positionId;
        const existing = playerState[playerId] ?? emptyRollingStats;
        return {
            ...playerState,
            [playerId]: {
                ...existing,
                correctTilesCount: existing.correctTilesCount - (correct ? 1 : 0),
                incorrectTilesCount: existing.incorrectTilesCount - (!correct ? 1 : 0),
                removedTilesCount: existing.removedTilesCount + 1,
            },
        };
    }
    throw new Error(`Unrecognised event ${JSON.stringify(action)}`);
}

function updateStatsForRemovedPlayer(rollingStatsMap: RollingStatsMap, removal: RemovePlayerEvent) {
    const {removePlayerId, replacementId} = removal;

    const oldState = rollingStatsMap[removePlayerId];
    if (!oldState) return; // nothing to merge / delete

    if (replacementId) {
        // merge into the replacement (or create a fresh entry)
        const target = {...(rollingStatsMap[replacementId] ?? emptyRollingStats)};
        // additive merge – counts are summed
        target.dealtTiles = [...target.dealtTiles, ...oldState.dealtTiles];
        target.correctTilesCount += oldState.correctTilesCount;
        target.incorrectTilesCount += oldState.incorrectTilesCount;
        target.removedTilesCount += oldState.removedTilesCount;
        target.crunchTimePlays += oldState.crunchTimePlays;

        // Take the latest Sequence.  Probably never happens...
        if (oldState.handEmptySeq !== undefined) {
            target.handEmptySeq = Math.max(
                target.handEmptySeq ?? 0,
                oldState.handEmptySeq,
            );
        }
        if (oldState.allAccurateSeq !== undefined) {
            target.allAccurateSeq = Math.max(
                target.allAccurateSeq ?? 0,
                oldState.allAccurateSeq,
            );
        }
        rollingStatsMap[replacementId] = target;
    }
    // Always clear the removed player.
    delete rollingStatsMap[removePlayerId];
}

export const playerStatsProjection: Projection<PlayerStatsSlice> = {
    name: 'playerStatsProjection',
    project: (
        state: PlayerStatsSlice,
        newEvents: GameEvent[],
    ): Partial<PlayerStatsSlice> => {

        // ----- start from the previous slice values -----
        let rollingStatsMap: RollingStatsMap = {...(state.rollingStatsMap ?? {})};
        let crunched = state.crunched ?? false;

        // ----- incremental processing of the new tile events -----
        newEvents
            .filter(isPlayerAction)
            .forEach(ev => {
                if (isCrunchTimePayload(ev.payload)) {
                    crunched = true;
                    return;
                }
                if (!isTilePayload(ev.payload))
                    return;
                rollingStatsMap = updateState(rollingStatsMap, ev, crunched);
            });

        // Update PlayerIDs if any players have been replaced.
        const removals = newEvents.filter(isRemovePlayerEvent);
        removals.forEach(removal => {
            updateStatsForRemovedPlayer(rollingStatsMap, removal);
        });

        return {
            rollingStatsMap,
            crunched,
        };
    },
    reset: () => ({
        rollingStatsMap: {},   // start with an empty map
        crunched: false,
    }),
};

export const useDealtTiles = (playerId: PlayerId): TileId[] => {
    const map = useGameStore((s:PlayerStatsSlice) => s.rollingStatsMap);
    return map?.[playerId]?.dealtTiles ?? [];
};

export function usePlayerStats(): FinalStatsMap {
    const rollingStatsMap = useGameStore((s: PlayerStatsSlice) => s.rollingStatsMap ?? {});

    // The memo runs only when the underlying state changes – no need for the full GameAction array.
    return useMemo(() => {
        const stats: FinalStatsMap = {};
        Object.entries(rollingStatsMap).forEach(([pid, st]) => {
            stats[pid] = {
                handEmptySequence: st.handEmptySeq ?? 0,
                allAccurateSequence: st.allAccurateSeq ?? 0,
                removedTilesCount: st.removedTilesCount,
                crunchTimeCount: st.crunchTimePlays,
            };
        });

        return stats;
    }, [rollingStatsMap]);
}
